/**
 * Token Refresh Error Detection and Handling
 * Detects invalid/expired refresh tokens and prevents retry loops
 */

export class InvalidRefreshTokenError extends Error {
  constructor(message: string = 'Invalid or expired refresh token') {
    super(message);
    this.name = 'InvalidRefreshTokenError';
  }
}

/**
 * Detects if an error is due to invalid/expired refresh token
 * Auth0 common error responses:
 * - "invalid_grant" error code
 * - "Refresh token is invalid or has expired" message
 * - "invalid refresh token" in description
 */
export const isInvalidRefreshTokenError = (error: any): boolean => {
  if (!error) return false;

  const message = (error?.error_description || error?.message || '').toLowerCase();
  const errorCode = (error?.error || '').toLowerCase();

  // Check Auth0 specific error codes
  if (errorCode === 'invalid_grant') {
    return true;
  }

  // Check common error message patterns
  const invalidPatterns = [
    'invalid refresh token',
    'refresh token is invalid',
    'refresh token expired',
    'refresh token has expired',
    'invalid_token',
  ];

  return invalidPatterns.some((pattern) => message.includes(pattern));
};

/**
 * Get user-friendly error message based on error type
 */
export const getTokenErrorMessage = (error: any): string => {
  if (isInvalidRefreshTokenError(error)) {
    return 'Your session has expired. Please log in again.';
  }
  return 'Authentication failed. Please log in again.';
};

/**
 * Tracks invalid token attempts to prevent infinite retry loops
 * Uses sessionStorage so flag resets on new browser session
 */
class InvalidTokenTracker {
  private readonly KEY = 'myassets_invalid_token_detected';

  isTokenInvalid(): boolean {
    try {
      const stored = sessionStorage.getItem(this.KEY);
      return stored === 'true';
    } catch (e) {
      console.warn('Failed to read invalid token flag from sessionStorage', e);
      return false;
    }
  }

  markTokenAsInvalid(): void {
    try {
      sessionStorage.setItem(this.KEY, 'true');
    } catch (e) {
      console.warn('Failed to write invalid token flag to sessionStorage', e);
    }
  }

  clearInvalidTokenFlag(): void {
    try {
      sessionStorage.removeItem(this.KEY);
    } catch (e) {
      console.warn('Failed to clear invalid token flag from sessionStorage', e);
    }
  }
}

export const tokenTracker = new InvalidTokenTracker();
